import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { usePractitioner } from 'src/careflow/ph-core/fhir-client/fhir-react';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import VerifiedDecorator from './VerifiedDecorator';
import { formatDate, getAge, getTimeDiff } from 'src/utils/dates';
import moment from 'moment';
import { Option } from 'src/@types/crs/case';
import PreviewDetailDrawer, {
  InfoRow,
  InfoRowValueList,
  PreviewDetailDrawerProps,
  SectionInfo,
} from 'src/careflow/common/PreviewDetailDrawer';
import { orderTelecomByRank } from 'src/utils/fhir';
import { formatUSAddress } from 'src/utils/address';
import { px } from 'src/components/CustomDrawer';
import { CRS_PATH } from 'src/routes/paths';
import PractitionerProfileImage from './PractitionerDetails/PractitionerProfileImage';
import { getFormattedPersonDOB } from 'src/careflow/YoCare/utils/personHelpers';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';

export interface PractitionerDrawerProps extends Omit<PreviewDetailDrawerProps, 'title'> {
  practitioner?: WrappedPractitioner | null;
  practitionerId?: string;
}

const commonVerticalSpacing = 2.3;

const PractitionerDrawer = ({
  practitioner,
  practitionerId,
  onClose,
  containerSx,
  ...drawerProps
}: PractitionerDrawerProps) => {
  const { i18n } = useLocales();
  const { configurations } = useTenantConfigData();
  const [practitionerResource, { isFetching }] = usePractitioner(practitionerId, {
    map: PractitionerWrapper,
    autofetch: !!practitionerId,
  });

  const practitionerFhir = (practitioner ?? practitionerResource) as WrappedPractitioner;

  const identifiers: Option<string>[] = [
    ...(practitionerFhir?.getNPI?.()
      ? [
          {
            label: 'npi',
            value: practitionerFhir.getNPI(),
          },
        ]
      : []),
    ...(practitionerFhir?.getInternalIdenfitier?.(configurations?.provider)
      ? [
          {
            label: 'internal',
            value: practitionerFhir.getInternalIdenfitier(configurations?.provider),
          },
        ]
      : []),
    ...(practitionerFhir?.getSSN?.()
      ? [
          {
            label: 'ssn',
            value: practitionerFhir.getSSN(),
          },
        ]
      : []),
    ...(practitionerFhir?.getMedicare?.()
      ? [
          {
            label: 'medicare',
            value: practitionerFhir.getMedicare(),
          },
        ]
      : []),
  ];

  const phones = orderTelecomByRank(practitionerFhir?.getPhones?.());

  const emails = orderTelecomByRank(practitionerFhir?.getEmails?.());

  const addresses = practitionerFhir?.address ?? [];
  const createdOn = practitionerFhir?.meta?.tag?.find?.(
    ({ code }) => code === configurations?.provider?.metaTagCreatedOnCode
  )?.display;

  return (
    <PreviewDetailDrawer
      showTitleDivider={false}
      isLoading={isFetching}
      containerSx={{ width: '20vw', minWidth: 320 }}
      contentContainerSx={{ backgroundColor: '#ffffff' }}
      anchor="right"
      {...drawerProps}
    >
      <Box sx={{ marginTop: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <PractitionerProfileImage
            practitionerProfileImage={practitionerFhir?.photo?.[0]}
            isEditable={false}
            avatarSx={{ width: 65, height: 65 }}
          />
          <Box sx={{ marginTop: commonVerticalSpacing }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', marginRight: 1 }}>
                {practitionerFhir?.getFullName?.() ?? ''}
              </Typography>

              <VerifiedDecorator isVerified={!!practitionerFhir?.isVerified?.(configurations?.provider)} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8rem', color: '#637381', textTransform: 'capitalize' }}
              >
                {practitionerFhir?.gender ?? ''}
              </Typography>
              {practitionerFhir?.birthDate ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      marginX: 1,
                      color: '#637381',
                      fontSize: '1.5rem',
                      position: 'relative',
                      top: -7,
                    }}
                  >
                    •
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#637381' }}>
                    {`${getAge(moment(practitionerFhir.birthDate ?? null))} years`}
                  </Typography>
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Button
          sx={{ height: 48, marginTop: 1 }}
          color="primary"
          fullWidth
          variant="contained"
          onClick={() => {
            window.open(`${CRS_PATH.provider.root}/${practitionerFhir?.id}`, '_blank');
          }}
        >
          {i18n('goToProfile', 'crs')}
        </Button>
        <Divider sx={{ borderStyle: 'dashed', marginTop: commonVerticalSpacing }} />
        <Box sx={{ marginTop: commonVerticalSpacing }}>
          {!!identifiers.length && (
            <SectionInfo title={i18n('practitioner.identifiersText', 'crs')}>
              {identifiers.map(({ label, value }) => (
                <InfoRow
                  key={`${label}-${value}-key`}
                  label={i18n(`practitioner.identifiers.${label}`, 'crs')}
                  value={value}
                />
              ))}
            </SectionInfo>
          )}
          <SectionInfo sx={{ paddingRight: px }} title={i18n('practitioner.general', 'crs')}>
            <TelecomInfoRowList
              label={i18n('practitioner.phones', 'crs')}
              values={phones.map(({ value }) => value)}
              resourceId={practitionerFhir?.id}
              system="phone"
              tooltip="Open practitioner phone in iComx"
            />
            <TelecomInfoRowList
              label={i18n('practitioner.emails', 'crs')}
              values={emails.map(({ value }) => value)}
              resourceId={practitionerFhir?.id}
              system="email"
              tooltip="Open practitioner email in iComx"
            />
            <InfoRowValueList
              label={i18n('practitioner.addresses', 'crs')}
              values={addresses.map((address) => formatUSAddress(address))}
              valueSx={{ whiteSpace: 'pre-line' }}
            />
            <InfoRow
              label={i18n(`practitioner.dob`, 'crs')}
              value={
                practitionerFhir?.birthDate
                  ? getFormattedPersonDOB(practitionerFhir?.birthDate)
                  : ''
              }
            />
            <InfoRowValueList
              label={i18n('practitioner.languages', 'crs')}
              values={
                practitionerFhir?.communication?.map?.(
                  ({ coding, text }) => coding?.[0]?.display ?? text ?? ''
                ) ?? []
              }
            />
            {!!createdOn && (
              <InfoRow
                label={i18n(`practitioner.added`, 'crs')}
                value={`${formatDate(moment(createdOn))} (${
                  getTimeDiff(moment(createdOn), moment())?.diffText
                })`}
              />
            )}
            {!!practitionerFhir?.meta?.lastUpdated && (
              <InfoRow
                label={i18n(`practitioner.modified`, 'crs')}
                value={`${formatDate(moment(practitionerFhir.meta.lastUpdated))} (${
                  getTimeDiff(moment(practitionerFhir.meta.lastUpdated), moment())?.diffText
                })`}
              />
            )}
          </SectionInfo>
        </Box>
      </Box>
    </PreviewDetailDrawer>
  );
};

interface TelecomInfoRowListProps {
  label: string;
  values: Array<string | undefined | null>;
  resourceId?: string;
  system: 'phone' | 'email';
  tooltip: string;
}

function TelecomInfoRowList({
  label,
  values,
  resourceId,
  system,
  tooltip,
}: TelecomInfoRowListProps) {
  const filteredValues = values.filter((value): value is string => !!value);

  if (!filteredValues.length) {
    return <InfoRowValueList label={label} values={[]} />;
  }

  return (
    <InfoRow
      label={label}
      value={
        <Stack spacing={1}>
          {filteredValues.map((value, index) => (
            <Box key={`${system}-${value}-${index}`} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography color="#637381">{value}</Typography>
              <IcomxInlineTelecomAction
                resourceType="Practitioner"
                resourceId={resourceId}
                system={system}
                tooltip={tooltip}
                value={value}
              />
            </Box>
          ))}
        </Stack>
      }
    />
  );
}

export default PractitionerDrawer;
